
import React from "react";
import { NavigationMenuItem, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TabContentImage } from "./TabContent";

interface ProductsMenuProps {
  activeTab: string;
  onTabChange: (value: string) => void;
  allImagesLoaded: boolean;
}

export const ProductsMenu: React.FC<ProductsMenuProps> = ({
  activeTab,
  onTabChange,
  allImagesLoaded
}) => {
  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger className="bg-transparent hover:bg-gray-200 text-primary text-sm font-medium">Products</NavigationMenuTrigger>
      <NavigationMenuContent className="w-[95vw] min-w-[95vw] max-w-[1000px] h-[500px] bg-white rounded-md shadow-lg p-4 border border-gray-200">
        <Tabs value={activeTab} onValueChange={onTabChange} orientation="vertical" className="flex">
          <TabsList className="flex flex-col w-1/4 h-[450px] bg-gray-50 rounded-l-md space-y-1 p-2 border-r border-primary portrait:w-2/5 portrait:min-w-[160px]">
            <TabsTrigger value="hr" className="w-full text-left pl-3 pr-3 text-primary-dark data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:rounded-none min-w-[100px] portrait:min-w-[100px] portrait:text-xs portrait:px-2 portrait:w-full portrait:text-left portrait:justify-start">HR module</TabsTrigger>
            <TabsTrigger value="sales" className="w-full text-left pl-3 pr-3 text-primary-dark data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:rounded-none min-w-[100px] portrait:min-w-[100px] portrait:text-xs portrait:px-2 portrait:w-full portrait:text-left portrait:justify-start">Sales module</TabsTrigger>
            <TabsTrigger value="marketing" className="w-full text-left pl-3 pr-3 text-primary-dark data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:rounded-none min-w-[100px] portrait:min-w-[100px] portrait:text-xs portrait:px-2 portrait:w-full portrait:text-left portrait:justify-start">Marketing module</TabsTrigger>
            <TabsTrigger value="finance" className="w-full text-left pl-3 pr-3 text-primary-dark data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:rounded-none min-w-[100px] portrait:min-w-[100px] portrait:text-xs portrait:px-2 portrait:w-full portrait:text-left portrait:justify-start">Finance module</TabsTrigger>
            <TabsTrigger value="projects" className="w-full text-left pl-3 pr-3 text-primary-dark data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:rounded-none min-w-[100px] portrait:min-w-[100px] portrait:text-xs portrait:px-2 portrait:w-full portrait:text-left portrait:justify-start">Projects module</TabsTrigger>
            <TabsTrigger value="performance" className="w-full text-left pl-3 pr-3 text-primary-dark data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:rounded-none min-w-[100px] portrait:min-w-[100px] portrait:text-xs portrait:px-2 portrait:w-full portrait:text-left portrait:justify-start">Performance module</TabsTrigger>
            <TabsTrigger value="legal" className="w-full text-left pl-3 pr-3 text-primary-dark data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:rounded-none min-w-[100px] portrait:min-w-[100px] portrait:text-xs portrait:px-2 portrait:w-full portrait:text-left portrait:justify-start">Legal module</TabsTrigger>
          </TabsList>
          <TabsContent value="hr" className="w-2/3 p-4 portrait:w-3/5 portrait:p-2 overflow-auto max-h-[400px] portrait:max-h-[350px]">
            <div className="flex flex-col items-center justify-center h-full">
              <TabContentImage 
                src="/lovable-uploads/HR_module.jpg"
                alt="HR"
                className="w-70 h-70 object-cover rounded-md mb-4"
                allImagesLoaded={allImagesLoaded}
              />
              <p className="text-sm text-primary-dark text-center">Streamline talent acquisition requirements, define organization structure and roles, find candidates using AI automation.</p>
            </div>
          </TabsContent>
          <TabsContent value="sales" className="w-2/3 p-4 portrait:w-3/5 portrait:p-2 overflow-auto max-h-[450px]">
            <div className="flex flex-col items-center justify-center h-full">
              <TabContentImage 
                src="/lovable-uploads/Sales_module.jpg"
                alt="Sales"
                className="w-70 h-70 object-cover rounded-md mb-4"
                allImagesLoaded={allImagesLoaded}
              />
              <p className="text-sm text-primary-dark text-center">Boost sales performance through building an effective Account Business Plan with deep AI-assisted customer insights.</p>
            </div>
          </TabsContent>
          <TabsContent value="marketing" className="w-2/3 p-4 portrait:w-3/5 portrait:p-2 overflow-auto max-h-[450px]">
            <div className="flex flex-col items-center justify-center h-full">
              <TabContentImage 
                src="/lovable-uploads/Marketing_module.jpg"
                alt="Marketing"
                className="w-70 h-70 object-cover rounded-md mb-4"
                allImagesLoaded={allImagesLoaded}
              />
              <p className="text-sm text-primary-dark text-center">Plan marketing budgets, analyze online sentiments, create compelling content, and drive effective GTM of the 4 P's of Marketing</p>
            </div>
          </TabsContent>
          <TabsContent value="finance" className="w-2/3 p-4 portrait:w-3/5 portrait:p-2 overflow-auto max-h-[450px]">
            <div className="flex flex-col items-center justify-center h-full">
              <TabContentImage 
                src="/lovable-uploads/Finance_module.jpg"
                alt="Finance"
                className="w-70 h-70 object-cover rounded-md mb-4"
                allImagesLoaded={allImagesLoaded}
              />
              <p className="text-sm text-primary-dark text-center">AI-empowered finance insights into sales performance, forecast and revenue trends, and intelligent gap analysis.</p>
            </div>
          </TabsContent>
          <TabsContent value="projects" className="w-2/3 p-4 portrait:w-3/5 portrait:p-2 overflow-auto max-h-[450px]">
            <div className="flex flex-col items-center justify-center h-full">
              <TabContentImage 
                src="/lovable-uploads/Projects_module.jpg"
                alt="Projects"
                className="w-70 h-70 object-cover rounded-md mb-4"
                allImagesLoaded={allImagesLoaded}
              />
              <p className="text-sm text-primary-dark text-center">AI-empowered projects guidance into project planning using templates, gantt charts, resource management with AI-powered projections.</p>
            </div>
          </TabsContent>
          <TabsContent value="performance" className="w-2/3 p-4 portrait:w-3/5 portrait:p-2 overflow-auto max-h-[450px]">
            <div className="flex flex-col items-center justify-center h-full">
              <TabContentImage 
                src="/lovable-uploads/Performance2_module.jpg"
                alt="Performance"
                className="w-70 h-70 object-cover rounded-md mb-4"
                allImagesLoaded={allImagesLoaded}
              />
              <p className="text-sm text-primary-dark text-center">Manage employee performance through goal setting, appraisals, performance banding, and improvement planning with AI-powered insights.</p>
            </div>
          </TabsContent>
          <TabsContent value="legal" className="w-2/3 p-4 portrait:w-3/5 portrait:p-2 overflow-auto max-h-[450px]">
            <div className="flex flex-col items-center justify-center h-full">
              <TabContentImage 
                src="/lovable-uploads/Legal_module.jpg"
                alt="Legal"
                className="w-70 h-70 object-cover rounded-md mb-4"
                allImagesLoaded={allImagesLoaded}
              />
              <p className="text-sm text-primary-dark text-center">Examine local laws, past cases and precedents, and compare country laws using advanced AI assistance.</p>
            </div>
          </TabsContent>
        </Tabs>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
};
